import { Button, Grid, IconButton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import GKClientDataGrid from "components/GKClientDataGrid";
import dayjs from "dayjs";
import { useState } from "react";
import { AiFillDelete } from "react-icons/ai";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import AddCorporateActionModal from "./AddCorporateActionModal";

function CorporateActions() {
  const { id } = useParams();
  const [isOpenCorporateAction, setIsOpenCorporateAction] = useState(false);

  const {
    state: { currentUser },
  } = useAppContext();

  const isDisabled = currentUser?.phone === "9911223344";

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["ADMIN_INSTRUMENT_CA"],
    queryFn: async () => axiosInstance.get(`/admin-instrument-ca/${id}/`),
    onError: () => {},
    refetchInterval: 60 * 1000,
  });

  const corporateColumns = [
    {
      headerName: "Date",
      field: "date",
      flex: 1,
      disableColumnMenu: true,
      minWidth: 80,
      valueGetter: (row) => dayjs(row?.row?.date).format("DD/MM/YYYY"),
    },
    {
      headerName: "Action Type",
      field: "type",
      flex: 1,
      disableColumnMenu: true,
      minWidth: 80,
    },
    {
      headerName: "Details",
      field: "details",
      flex: 1,
      disableColumnMenu: true,
      minWidth: 400,
      renderCell: (row) => {
        const renderDetails = () => {
          const { type, ...rest } = row?.row || {};
          switch (type) {
            case "dividend":
              return (
                <Typography>
                  <b>Dividend Per Share:</b> {rest?.dividendPerShare}
                </Typography>
              );
            case "split":
            case "bonus":
            case "reverse_split":
            case "right_issue":
              return (
                <Grid container>
                  <Grid xs={12}>
                    <Typography>
                      <b>For Stocks: </b>
                      {rest?.forStocks}
                    </Typography>
                  </Grid>
                  <Grid xs={12}>
                    <Typography>
                      <b>Issued Stocks:</b> {rest?.issuedStocks}
                    </Typography>
                  </Grid>
                </Grid>
              );
            case "demerger":
              return (
                <Grid container>
                  <Grid xs={12}>
                    <Typography>
                      <b>Existing Asset Quantity: </b>
                      {rest?.existingAssetQuantity}
                    </Typography>
                  </Grid>
                  <Grid xs={12}>
                    <Typography>
                      <b>New Asset Quantity:</b>
                      {rest?.newAssetQuantity}
                    </Typography>
                  </Grid>
                  <Grid xs={12}>
                    <Typography>
                      <b>Cost Acquisition Ratio:</b>
                      {rest?.costAcquisitionRatio}
                    </Typography>
                  </Grid>
                  {rest?.demergedCompanies?.map((company, index) => (
                    <Grid item container key={index}>
                      <Grid xs={12}>
                        <Typography>
                          <b>Demerged Company:</b> {company.childCompany}
                        </Typography>
                      </Grid>
                      <Grid xs={12}>
                        <Typography className="flex flex-row gap-2">
                          <b>New Asset Quantity:</b> {company.newAssetQuantity}
                        </Typography>
                      </Grid>
                      <Grid xs={12}>
                        <Typography className="flex flex-row gap-2">
                          <b>Cost Acquisition Ratio:</b>{" "}
                          {company.costAcquisitionRatio}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              );
            case "merger":
              return (
                <Grid container>
                  <Grid xs={12}>
                    <b>Existing Asset Quantity:</b>{" "}
                    {rest?.existingAssetQuantity}
                  </Grid>
                  <Grid xs={12}>
                    <b>New Asset Quantity:</b> {rest?.newAssetQuantity}
                  </Grid>
                  <Grid xs={12}>
                    <b>Parent Company:</b> {rest?.parentCompany}
                  </Grid>
                </Grid>
              );
            case "REC":
              return (
                <Grid container>
                  <Grid xs={12}>
                    <b>premium:</b> {rest?.premium}
                  </Grid>
                  <Grid xs={12}>
                    <b>closingDate:</b>{" "}
                    {dayjs(rest?.closingDate).format("DD/MM/YYYY")}
                  </Grid>
                  <Grid xs={12}>
                    <b>Patly Paid Instrument:</b> {rest?.reGcode}
                  </Grid>
                </Grid>
              );
            default:
              return null;
          }
        };

        return <>{renderDetails()}</>;
      },
    },
    {
      headerName: "Purpose",
      field: "purpose",
      flex: 1,
      disableColumnMenu: true,
      minWidth: 150,
      // valueGetter: (params) => params?.row?.purpose || "-",
    },

    {
      headerName: "Action",
      field: "action",
      flex: 1,
      disableColumnMenu: true,
      minWidth: 50,
      renderCell: (params) => {
        return (
          <IconButton
            disabled={isDisabled}
            onClick={async () => {
              axiosInstance
                .delete(`/admin-instrument-ca/${id}/delete`, {
                  params: {
                    ca_id: params.row.caId,
                    act_type: params.row.type,
                  },
                })
                .then(() => {
                  refetch();
                })
                .catch(() => {});
            }}
          >
            <AiFillDelete size={20} />
          </IconButton>
        );
      },
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <GKClientDataGrid
          loading={isFetching}
          columns={corporateColumns}
          headerComponent={
            <Button
              sx={{
                fontSize: "0.7rem",
                fontFamily: '"Arial", sans-serif',
                textTransform: "none",
              }}
              onClick={() => setIsOpenCorporateAction(!isOpenCorporateAction)}
              variant="contained"
              disabled={isDisabled}
            >
              Add Corporate Action
            </Button>
          }
          rows={
            data?.data?.data?.map((data, index) => {
              return {
                ...data,
                id: index,
              };
            }) || []
          }
          rowHeight={150}
        />
      </Grid>

      {isOpenCorporateAction && (
        <AddCorporateActionModal
          isModalOpen={isOpenCorporateAction}
          setIsModalOpen={setIsOpenCorporateAction}
          refetch={refetch}
          isFetching={isFetching}
        />
      )}
    </Grid>
  );
}

export default CorporateActions;
